import type { CatalogRoomType, PropertyCatalog } from '@/api/core/properties';
import type { MediaGroup, MediaTarget, MediaTargetKind } from '@/api/core/property-media';
import { isApiError } from '@/api/http/errors';
import { LoadFailure } from '@/components/load-failure';
import { LoadMoreFooter } from '@/components/load-more-footer';
import { LoadingAnnouncement } from '@/components/loading-announcement';
import {
  Button,
  DataTable,
  DetailPage,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  type DataTableColumn,
} from '@mgrvtechgroup/admin-ds';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import type { PropertyRuntime } from '../property-queries';
import type { PropertyMediaState } from '../property-url';
import { MediaItemsPanel } from './media-items-panel';
import {
  categoryLabel,
  languageLabel,
  mediaTargetLabel,
  publicationSummary,
  purposeLabel,
  TARGET_KIND_LABEL,
} from './media-presentation';
import { propertyMediaGroupsQuery } from './media-queries';

const SUBJECT = '사진·영상';
const PROPERTY_TARGET: MediaTarget = { kind: 'PROPERTY', id: null, floor: null };
const FLOOR_PATTERN = /^-?\d{1,9}$/;

export interface MediaPatch {
  target?: MediaTarget;
  groupId?: string | null;
}

function uniqueById(groups: MediaGroup[]): MediaGroup[] {
  const seen = new Set<string>();
  return groups.filter((group) => {
    if (seen.has(group.id)) return false;
    seen.add(group.id);
    return true;
  });
}

function groupColumns(): DataTableColumn<MediaGroup>[] {
  return [
    {
      key: 'purpose',
      sortable: false,
      header: '용도',
      cellClassName: 'whitespace-normal',
      cell: (group) => (
        <span className="flex min-w-0 flex-col">
          <span>{purposeLabel(group.purpose)}</span>
          <span className="caption text-muted-foreground">{group.purpose}</span>
        </span>
      ),
    },
    {
      key: 'language',
      sortable: false,
      header: '언어',
      cell: (group) => languageLabel(group.language),
    },
    {
      key: 'category',
      sortable: false,
      header: '분류',
      cellClassName: 'whitespace-normal',
      cell: (group) => {
        const label = categoryLabel(group.categoryName);
        // 알려진 분류 코드는 풀어 쓰고 저장된 원문을 함께 남긴다.
        return (
          <span className="flex min-w-0 flex-col">
            <span className="break-words">{label}</span>
            {group.categoryName && group.categoryName !== label ? (
              <span className="caption text-muted-foreground break-words">
                {group.categoryName}
              </span>
            ) : null}
          </span>
        );
      },
    },
    {
      key: 'order',
      sortable: false,
      header: '순서',
      cell: (group) => <span className="tabular-nums">{group.sourceOrder ?? '없음'}</span>,
    },
    {
      key: 'component',
      sortable: false,
      header: '구성 요소',
      cellClassName: 'whitespace-normal',
      cell: (group) => <span className="break-words">{group.componentKey ?? '—'}</span>,
    },
    {
      key: 'observed',
      sortable: false,
      header: '기록 상태',
      cellClassName: 'whitespace-normal',
      cell: (group) => {
        return (
          <span className="caption text-muted-foreground">
            {group.observationState ?? '확인 불가'}
          </span>
        );
      },
    },
  ];
}

/**
 * 대상(지점·층·객실 유형·객실·슬롯). 객실·슬롯은 id를 찾을 검색이 없어 객실 탭의
 * "사진·영상 보기"로만 들어오고, 여기서는 현재 대상으로 보여 주며 풀 수 있다.
 * 종류를 바꾸면 값을 고를 때까지는 임시 선택이고, 값이 정해져야 주소(조회)에 반영된다.
 */
function MediaFilters({
  target,
  roomTypes,
  targetLabel,
  onChange,
}: {
  target: MediaTarget;
  roomTypes: readonly CatalogRoomType[];
  targetLabel: string;
  onChange: (patch: MediaPatch) => void;
}) {
  const [draftKind, setDraftKind] = useState<MediaTargetKind>(target.kind);
  const [floorText, setFloorText] = useState(target.floor === null ? '' : String(target.floor));
  const [floorError, setFloorError] = useState<string | null>(null);
  const kindItems = [
    { value: 'PROPERTY', label: TARGET_KIND_LABEL.PROPERTY },
    { value: 'PROPERTY_FLOOR', label: TARGET_KIND_LABEL.PROPERTY_FLOOR },
    { value: 'ROOM_TYPE', label: TARGET_KIND_LABEL.ROOM_TYPE },
    // 객실·슬롯은 진입 링크로만 고를 수 있다 — 현재 대상일 때만 보여 준다.
    ...(target.kind === 'ROOM' || target.kind === 'SLOT'
      ? [{ value: target.kind, label: TARGET_KIND_LABEL[target.kind] }]
      : []),
  ];
  const typeItems = roomTypes.map((type) => ({ value: type.id, label: type.name }));
  const changed = target.kind !== 'PROPERTY';
  const applyFloor = () => {
    const text = floorText.trim();
    if (!FLOOR_PATTERN.test(text)) {
      setFloorError('층은 정수로 입력해 주세요. 지하는 -1처럼 입력합니다.');
      return;
    }
    setFloorError(null);
    onChange({ target: { kind: 'PROPERTY_FLOOR', id: null, floor: Number(text) } });
  };
  return (
    <div className="flex flex-wrap items-end gap-3">
      <div className="flex min-w-0 flex-col gap-1.5">
        <Label htmlFor="media-target-kind">대상</Label>
        <Select
          value={draftKind}
          items={kindItems}
          onValueChange={(value) => {
            if (value === null) return;
            setDraftKind(value);
            setFloorError(null);
            if (value === 'PROPERTY') onChange({ target: PROPERTY_TARGET });
          }}
        >
          <SelectTrigger id="media-target-kind" aria-label="대상 종류" className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {kindItems.map((item) => (
              <SelectItem key={item.value} value={item.value}>
                {item.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {draftKind === 'PROPERTY_FLOOR' ? (
        <div className="flex min-w-0 flex-col gap-1.5">
          <Label htmlFor="media-floor">층</Label>
          <div className="flex items-center gap-2">
            <Input
              id="media-floor"
              inputMode="numeric"
              placeholder="예: 3, 0, -1"
              value={floorText}
              aria-invalid={floorError !== null}
              aria-describedby={floorError ? 'media-floor-error' : undefined}
              className="w-28"
              onChange={(event) => {
                setFloorText(event.target.value);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') applyFloor();
              }}
            />
            <Button variant="outline" onClick={applyFloor}>
              적용
            </Button>
          </div>
          {floorError ? (
            <p id="media-floor-error" role="alert" className="caption text-destructive-text">
              {floorError}
            </p>
          ) : null}
        </div>
      ) : null}
      {draftKind === 'ROOM_TYPE' ? (
        <div className="flex min-w-0 flex-col gap-1.5">
          <Label htmlFor="media-room-type">객실 유형</Label>
          <Select
            value={target.kind === 'ROOM_TYPE' ? target.id : null}
            items={typeItems}
            disabled={typeItems.length === 0}
            onValueChange={(value) => {
              if (value !== null)
                onChange({ target: { kind: 'ROOM_TYPE', id: value, floor: null } });
            }}
          >
            <SelectTrigger id="media-room-type" aria-label="객실 유형" className="w-48">
              <SelectValue
                placeholder={typeItems.length === 0 ? '객실 유형 없음' : '객실 유형 선택'}
              />
            </SelectTrigger>
            <SelectContent>
              {typeItems.map((item) => (
                <SelectItem key={item.value} value={item.value}>
                  {item.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : null}
      {target.kind === 'ROOM' || target.kind === 'SLOT' ? (
        <div className="flex min-w-0 flex-col gap-1.5">
          <span className="label">선택한 대상</span>
          <div className="flex items-center gap-2">
            <span className="body font-medium break-all">{targetLabel}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                onChange({ target: PROPERTY_TARGET });
              }}
            >
              지점 전체로
            </Button>
          </div>
        </div>
      ) : null}
      {changed ? (
        <Button
          variant="ghost"
          onClick={() => {
            onChange({ target: PROPERTY_TARGET });
          }}
        >
          초기화
        </Button>
      ) : null}
    </div>
  );
}

/**
 * 지점 상세의 "사진·영상" — Core에 저장된 한 공간의 사진 그룹과 항목, 저장된 공개 선택을 읽는다.
 * 서버는 공개 적격성을 계산하지 않고(NOT_EVALUATED) 우리도 판정하지 않는다.
 */
export function PropertyMediaSection({
  runtime,
  generation,
  propertyId,
  media,
  catalogs,
  isCurrent,
  onChange,
}: {
  runtime: PropertyRuntime;
  generation: number;
  propertyId: string;
  media: PropertyMediaState;
  /** 대상 이름을 풀고 객실 유형 목록을 만드는 데 쓰는 이미 불러온 카탈로그 */
  catalogs: readonly (PropertyCatalog | undefined)[];
  isCurrent: () => boolean;
  onChange: (patch: MediaPatch) => void;
}) {
  const { target, groupId } = media;
  const queryClient = useQueryClient();
  const options = propertyMediaGroupsQuery(
    runtime,
    generation,
    propertyId,
    target,
    true,
    isCurrent,
  );
  const query = useInfiniteQuery(options);
  const pages = query.data?.pages;
  const latest = pages?.at(-1);
  const groups = useMemo(() => uniqueById(pages?.flatMap((page) => page.groups) ?? []), [pages]);
  // 행을 누르면 그 그룹의 항목을 다이얼로그로 연다.
  const openGroup = useCallback(
    (group: MediaGroup) => {
      onChange({ groupId: group.id });
    },
    [onChange],
  );
  const tableColumns = useMemo(() => groupColumns(), []);
  const roomTypes = catalogs.find((catalog) => catalog !== undefined)?.roomTypes ?? [];
  const targetLabel = mediaTargetLabel(target, catalogs);
  const restart = () => {
    void queryClient.resetQueries({ queryKey: options.queryKey, exact: true });
  };
  const filtered = target.kind !== 'PROPERTY';
  const nextError = query.isFetchNextPageError ? query.error : null;
  const cursorLost =
    isApiError(nextError) && (nextError.status === 409 || nextError.status === 422);

  return (
    <div className="flex min-w-0 flex-col gap-6">
      <DetailPage.Section
        title={SUBJECT}
        description="지점·층·객실 유형·객실·슬롯별로 저장된 사진·영상 묶음과 항목입니다. 저장된 사실만 보여 주며 웹 공개 가능 여부는 판정하지 않습니다."
      >
        <MediaFilters
          key={JSON.stringify(target)}
          target={target}
          roomTypes={roomTypes}
          targetLabel={targetLabel}
          onChange={onChange}
        />
        {latest ? (
          <div
            aria-label="조회 요약"
            role="group"
            className="caption text-muted-foreground flex flex-wrap gap-x-6 gap-y-1"
          >
            <span>
              대상 <span className="text-foreground">{targetLabel}</span>
            </span>
            <span>
              공간 소개{' '}
              <span className="text-foreground">
                {latest.presentation
                  ? `있음 · 버전 ${String(latest.presentation.contentRevision)}`
                  : '없음'}
              </span>
            </span>
            <span>
              저장된 공개 선택{' '}
              <span className="text-foreground">
                {publicationSummary(
                  latest.publication?.status ?? null,
                  latest.publicationRecordState,
                )}
              </span>
            </span>
          </div>
        ) : null}
        {query.isError && !pages ? (
          <LoadFailure error={query.error} subject={SUBJECT} onRetry={() => void query.refetch()} />
        ) : (
          <>
            {query.isRefetchError ? (
              <LoadFailure error={query.error} subject={SUBJECT} onRetry={restart} />
            ) : null}
            {query.isPending ? (
              <LoadingAnnouncement>사진·영상 묶음을 불러오고 있습니다.</LoadingAnnouncement>
            ) : null}
            <div role="region" aria-label="사진·영상 묶음 표" className="min-w-0">
              <DataTable
                columns={tableColumns}
                data={groups}
                rowKey={(group) => group.id}
                isLoading={query.isPending}
                onRowClick={(group) => {
                  openGroup(group);
                }}
                empty={
                  latest?.presentation === null
                    ? {
                        title: filtered
                          ? '선택한 대상에 저장된 사진·영상 정보가 없습니다'
                          : '이 지점에 저장된 사진·영상 정보가 없습니다',
                        description: filtered
                          ? '대상을 바꾸거나 초기화해 지점 전체를 확인해 보세요. 이 대상의 공간 소개가 아직 없습니다.'
                          : '지점의 공간 소개가 아직 없습니다.',
                      }
                    : {
                        title: filtered
                          ? '선택한 대상에 사진·영상 묶음이 없습니다'
                          : '표시할 사진·영상 묶음이 없습니다',
                        description: filtered
                          ? '대상을 바꾸거나 초기화해 지점 전체를 확인해 보세요.'
                          : '공간 소개는 있으나 저장된 묶음이 없습니다.',
                      }
                }
              />
            </div>
            {latest && (groups.length > 0 || query.hasNextPage) ? (
              <LoadMoreFooter
                label="사진·영상 묶음"
                loaded={groups.length}
                total={latest.page.total}
                hasMore={query.hasNextPage}
                isFetching={query.isFetchingNextPage}
                unit="개 그룹"
                errorMessage={
                  nextError
                    ? cursorLost
                      ? '목록이 그사이 바뀌었습니다. 처음부터 다시 조회해 주세요.'
                      : '다음 묶음을 불러오지 못했습니다. 이미 불러온 묶음은 그대로입니다.'
                    : null
                }
                restart={cursorLost ? { label: '처음부터 다시 조회', onClick: restart } : null}
                onLoadMore={() => void query.fetchNextPage()}
              />
            ) : null}
          </>
        )}
      </DetailPage.Section>
      {groupId === null ? null : (
        <MediaItemsPanel
          key={groupId}
          runtime={runtime}
          generation={generation}
          propertyId={propertyId}
          groupId={groupId}
          isCurrent={isCurrent}
          scopeLabel={({ kind, id, floor }) =>
            mediaTargetLabel({ kind: kind as MediaTargetKind, id, floor }, catalogs)
          }
          onClose={() => {
            onChange({ groupId: null });
          }}
        />
      )}
    </div>
  );
}
